import moment from 'moment'
import { INSTRUMENT_RECURRING_SCHEME_MAP } from 'utils/constants'
import { fromUTCMidnightSeconds } from 'utils/time'
import { isScheduleVisitConfirmed } from '../../Visits/modules/visitUtils'
import {
  canTreatNullStartDateAsLive,
  getInstanceVisibleDays,
  getScheduleInstanceEntries,
  isInstanceActiveInCurrentWindow,
  isMultiScheduleMetadata,
  pickEarliestInstanceByStartDate,
  resolveSchedule,
} from './instrumentUtils'

const persistentUsesBoundedCalendarDays = (recurring) => {
  if (!isPersistentRecurringScheme(recurring)) return false
  if (isIntervalRecurringScheme(recurring)) return true
  return recurring?.recurrences != null
}

const usesAbsoluteRecurringCalendarDays = (recurring) =>
  isIntervalRecurringScheme(recurring) ||
  (isPersistentRecurringScheme(recurring) && recurring?.recurrences != null)

/** Participant first-login only — enrollment/created_on is not first login. */
export const getParticipantFirstLogin = (user) => user?.first_login ?? null

export const hasAbsoluteDeploy = (deploy) =>
  deploy?.hasOwnProperty('absolute') && deploy.absolute != null

export const hasNowDeploy = (deploy) =>
  Boolean(deploy?.hasOwnProperty('now')) && !hasAbsoluteDeploy(deploy)

/** Locale-safe calendar day key (always Latin digits for stable comparisons). */
export const getCalendarDayKey = (dayMoment) =>
  moment(dayMoment).clone().locale('en').format('YYYY-MM-DD')

export const getInstanceOccurrenceDayKey = (startDate) =>
  startDate
    ? moment.utc(startDate).clone().locale('en').format('YYYY-MM-DD')
    : null

export const getNowDeployDay = (instances, asOfDay = moment()) => {
  const starts = (instances || [])
    .map((instance) => {
      const dayKey = getInstanceOccurrenceDayKey(instance?.start_date)
      return dayKey ? moment.utc(dayKey, 'YYYY-MM-DD') : null
    })
    .filter(Boolean)

  if (starts.length) return moment.min(starts)

  return moment.utc(getCalendarDayKey(asOfDay), 'YYYY-MM-DD')
}

/** All API instances for the same schedule row (deploy.now / recurrence anchor). */
export const getScheduleScopedInstances = (instanceGroup, instance) => {
  const scheduleId = instance?.schedule_id || instance?.scheduleId
  const allInstances = instanceGroup?.instances || []

  if (!scheduleId) {
    return allInstances.length ? allInstances : instance ? [instance] : []
  }

  const scoped = allInstances.filter(
    (item) => (item.schedule_id || item.scheduleId) === scheduleId,
  )

  return scoped.length ? scoped : instance ? [instance] : []
}

export const getAbsoluteDeployDay = (deploy) => {
  if (!hasAbsoluteDeploy(deploy)) return null
  return fromUTCMidnightSeconds(deploy.absolute).startOf('day')
}

export const isIntervalRecurringScheme = (recurring) =>
  recurring?.scheme === 'n-days' ||
  recurring?.scheme === 'n-weeks' ||
  recurring?.scheme === 'n-months'

const getAbsoluteRecurringCycleLength = (recurring) => {
  const interval = recurring?.interval || 1
  if (recurring?.scheme === 'n-weeks') return interval * 7
  if (recurring?.scheme === 'n-months') return interval * 30
  return interval
}

const normalizeRecurringForAbsoluteCycles = (recurring) => {
  if (!recurring) return null
  if (isIntervalRecurringScheme(recurring)) return recurring
  if (
    isPersistentRecurringScheme(recurring) &&
    recurring?.recurrences != null
  ) {
    if (recurring.interval_type === 'weeks') {
      return { ...recurring, scheme: 'n-weeks' }
    }
    if (recurring.interval_type === 'months') {
      return { ...recurring, scheme: 'n-months' }
    }
    return { ...recurring, scheme: 'n-days' }
  }
  return null
}

/** Whether targetDay aligns with a recurrence cycle anchored at deploy.absolute. */
export const isAbsoluteRecurringVisibleOnDay = (
  anchorDay,
  recurring,
  targetDayMoment,
) => {
  const normalizedRecurring = normalizeRecurringForAbsoluteCycles(recurring)
  if (!anchorDay || !normalizedRecurring) {
    return false
  }

  const targetDay = moment.utc(
    getCalendarDayKey(targetDayMoment),
    'YYYY-MM-DD',
  )
  const anchor = moment.utc(getCalendarDayKey(anchorDay), 'YYYY-MM-DD')

  if (targetDay.isBefore(anchor, 'day')) return false

  const visibleDays = normalizedRecurring?.visible_days ?? 1
  const recurrences = normalizedRecurring?.recurrences

  if (normalizedRecurring?.scheme === 'n-months') {
    for (let index = 0; recurrences == null || index < recurrences; index += 1) {
      const cycleStart = anchorDay
        .clone()
        .add(index * (normalizedRecurring.interval || 1), 'months')
      const cycleEnd = cycleStart.clone().add(Math.max(visibleDays, 1) - 1, 'days')

      if (targetDay.isBefore(cycleStart, 'day')) return false
      if (targetDay.isBetween(cycleStart, cycleEnd, 'day', '[]')) {
        return true
      }
    }
    return false
  }

  const cycleLength = getAbsoluteRecurringCycleLength(normalizedRecurring)
  const daysSinceAnchor = targetDay.diff(anchor, 'days')
  const recurrenceIndex = Math.floor(daysSinceAnchor / cycleLength)
  const offsetInCycle = daysSinceAnchor % cycleLength

  if (recurrences != null && recurrenceIndex >= recurrences) return false

  return offsetInCycle < visibleDays
}

/**
 * Calendar visibility for deploy.absolute schedules.
 * Returns null when the schedule is not absolute-deploy; otherwise true/false.
 */
export const isAbsoluteDeployScheduleVisibleOnDay = (
  schedule,
  targetDayMoment,
  {
    isToday = false,
    isTimeOfDayValid = () => true,
    isExpired = () => false,
    firstLogin,
    areAllInstancesCompleted = false,
    instances = [],
  } = {},
) => {
  const { deploy, expire, recurring } = schedule || {}
  if (!hasAbsoluteDeploy(deploy)) return null

  const anchorDay = getAbsoluteDeployDay(deploy)
  if (!anchorDay) return null

  if (isExpired(expire, targetDayMoment, firstLogin)) return false

  const isPersistent = isPersistentRecurringScheme(recurring)
  if (
    !isPersistent &&
    (areAllInstancesCompleted ||
      hasCompletedOccurrenceOnCalendarDay(instances, targetDayMoment))
  ) {
    return false
  }

  if (isPersistent && !persistentUsesBoundedCalendarDays(recurring)) {
    if (targetDayMoment.isBefore(anchorDay, 'day')) return false
    if (
      isToday &&
      deploy.time_of_day &&
      !isTimeOfDayValid(deploy.time_of_day)
    ) {
      return false
    }
    return true
  }

  let isVisibleOnDay = false

  if (usesAbsoluteRecurringCalendarDays(recurring)) {
    if (recurring.recurrences != null) {
      isVisibleOnDay = isAbsoluteRecurringVisibleOnDay(
        anchorDay,
        recurring,
        targetDayMoment,
      )
    } else {
      const visibleDays = recurring.visible_days ?? 1
      const lastVisibleDay = anchorDay
        .clone()
        .add(Math.max(visibleDays, 1) - 1, 'days')
      isVisibleOnDay = targetDayMoment.isBetween(
        anchorDay,
        lastVisibleDay,
        'day',
        '[]',
      )
    }
  } else {
    isVisibleOnDay = targetDayMoment.isSame(anchorDay, 'day')
  }

  if (!isVisibleOnDay) return false

  if (isToday && deploy.time_of_day && !isTimeOfDayValid(deploy.time_of_day)) {
    return false
  }

  return true
}

/**
 * Calendar visibility for deploy.now schedules (immediate deploy).
 * Non-recurring never-expire instruments appear only on the instance start day.
 * Returns null when the schedule is not deploy.now; otherwise true/false.
 */
export const isNowDeployScheduleVisibleOnDay = (
  schedule,
  targetDayMoment,
  {
    isToday = false,
    isTimeOfDayValid = () => true,
    isExpired = () => false,
    firstLogin,
    areAllInstancesCompleted = false,
    instances = [],
    asOfDay = moment(),
  } = {},
) => {
  const { deploy, expire, recurring } = schedule || {}
  if (!hasNowDeploy(deploy)) return null

  if (isExpired(expire, targetDayMoment, firstLogin)) return false

  const isPersistent = isPersistentRecurringScheme(recurring)
  if (
    !isPersistent &&
    (areAllInstancesCompleted ||
      hasCompletedOccurrenceOnCalendarDay(instances, targetDayMoment))
  ) {
    return false
  }

  const anchorDay = getNowDeployDay(instances, asOfDay)

  if (isPersistent && !persistentUsesBoundedCalendarDays(recurring)) {
    if (targetDayMoment.isBefore(anchorDay, 'day')) return false
    if (
      isToday &&
      deploy.time_of_day &&
      !isTimeOfDayValid(deploy.time_of_day)
    ) {
      return false
    }
    return true
  }

  let isVisibleOnDay = false

  if (usesAbsoluteRecurringCalendarDays(recurring)) {
    if (recurring.recurrences != null) {
      isVisibleOnDay = isAbsoluteRecurringVisibleOnDay(
        anchorDay,
        recurring,
        targetDayMoment,
      )
    } else {
      const visibleDays = recurring.visible_days ?? 1
      const lastVisibleDay = anchorDay
        .clone()
        .add(Math.max(visibleDays, 1) - 1, 'days')
      isVisibleOnDay = targetDayMoment.isBetween(
        anchorDay,
        lastVisibleDay,
        'day',
        '[]',
      )
    }
  } else {
    isVisibleOnDay = targetDayMoment.isSame(anchorDay, 'day')
  }

  if (!isVisibleOnDay) return false

  if (isToday && deploy.time_of_day && !isTimeOfDayValid(deploy.time_of_day)) {
    return false
  }

  return true
}

export const getScheduleDeploymentDay = (deploy, firstLogin) => {
  if (!deploy) return null

  if (hasAbsoluteDeploy(deploy)) {
    return getAbsoluteDeployDay(deploy)
  }

  if (!firstLogin) return null

  if (deploy.hasOwnProperty('first_login')) {
    const interval = deploy.first_login?.interval || 0
    return moment(firstLogin).add(interval, 'days').startOf('day')
  }

  if (deploy.hasOwnProperty('days_offset') && deploy.days_offset === 0) {
    return moment(firstLogin).startOf('day')
  }

  return null
}

export const getEffectiveInstanceCalendarStartDay = (
  instanceGroup,
  instance,
  schedule,
  firstLogin,
  asOfDay = moment(),
) => {
  const fromInstance = getInstanceCalendarStartDay(
    instanceGroup,
    instance,
    schedule,
    asOfDay,
  )
  const deploymentDay = getScheduleDeploymentDay(schedule?.deploy, firstLogin)

  if (!fromInstance && !deploymentDay) return null
  if (!fromInstance) return deploymentDay
  if (!deploymentDay) return fromInstance

  return moment.max(fromInstance, deploymentDay)
}

export const isPersistentRecurringScheme = (recurring) =>
  recurring?.scheme === INSTRUMENT_RECURRING_SCHEME_MAP.persistent

export const isVisitDeployedInstrument = (schedule) =>
  Boolean(schedule?.chain_deployment_info?.chain_deployment_by_ss_visit)

export const hasConfirmedVisitInstance = (instances) =>
  (instances || []).some((instance) => Boolean(instance.start_date))

/**
 * Visit-triggered instruments stay hidden until confirmation, including
 * persistent / repeat-immediately. Persistent may omit start_date after the
 * visit is confirmed, so confirmed visit IDs are the source of truth then.
 */
export const shouldHideUnconfirmedVisitInstrument = (
  schedule,
  instances,
  confirmedVisitKeys,
) => {
  if (!isVisitDeployedInstrument(schedule)) return false
  if (hasConfirmedVisitInstance(instances)) return false
  if (isScheduleVisitConfirmed(schedule, confirmedVisitKeys)) return false
  return true
}

/**
 * Resolve which instance_id belongs to a calendar day the user tapped.
 * Uses start/end window for that day — not API index order and not
 * "earliest in the series". pickEarliest only breaks ties if multiple
 * rows match the same day.
 */
/**
 * Whether a pending calendar row would render in the expanded day panel.
 * Aligns day-dot colors with instruments the participant can actually open.
 * GOLD-12525 / GOLD-12528.
 */
export const shouldDisplayPendingInstrumentOnCalendarDay = (
  instrument,
  targetDayMoment,
  { allInstances, firstLogin } = {},
) => {
  const instrumentId =
    instrument?.instrument_id || instrument?.identity || instrument?.id
  const instanceGroup =
    allInstances?.find(
      (entry) =>
        (entry?.instrument_id || entry?.identity || entry?.id) === instrumentId,
    ) || instrument
  const instances = instrument?.instances || []
  const scheduleId =
    instrument?.schedule_id ||
    instances[0]?.schedule_id ||
    instances[0]?.scheduleId
  const schedule = resolveSchedule(
    instrument?.metadata?.schedule,
    scheduleId,
  )
  const isPersistent = isPersistentRecurringScheme(schedule?.recurring)
  const dayMoment = moment(targetDayMoment).startOf('day')

  const activeInstances = instances.filter((instance) => {
    if (!isPersistent && instance.completion) return false
    return isInstanceActiveInCurrentWindow(instance, schedule)
  })

  const calendarDayInstanceId = resolveInstanceIdForCalendarDay({
    instanceGroup,
    instances,
    targetDayMoment: dayMoment,
    isPersistent,
    schedule,
    firstLogin,
  })

  if (!isPersistent && !calendarDayInstanceId && !activeInstances.length) {
    return false
  }

  if (
    !isPersistent &&
    getInstancesCompletedForCalendarDay(instances, dayMoment).length
  ) {
    return false
  }

  const instanceId =
    calendarDayInstanceId ||
    (!isPersistent ? activeInstances[0]?.instance_id : null) ||
    instrument?.instance_id

  if (!instanceId) return false

  const instanceForSelectedDay = instances.find(
    (instance) => instance.instance_id === instanceId,
  )

  if (instanceForSelectedDay?.completion) return false

  if (
    !instanceForSelectedDay &&
    !isPersistent &&
    !activeInstances.length
  ) {
    return false
  }

  return true
}

export const resolveInstanceIdForCalendarDay = ({
  instanceGroup,
  instances = [],
  targetDayMoment,
  isPersistent = false,
  schedule,
  firstLogin,
}) => {
  const dayMoment = moment(targetDayMoment).startOf('day')
  const pool = (instances || []).filter((instance) => !instance.completion)
  const group = instanceGroup || { instances }

  const matching = pool.filter((instance) => {
    if (schedule) {
      return isInstanceVisibleOnCalendarDay(
        group,
        instance,
        dayMoment,
        schedule,
        moment(),
        firstLogin,
      )
    }

    if (!instance?.start_date) return false
    return dayMoment.isBetween(
      instance.start_date,
      instance.end_date || instance.start_date,
      'day',
      '[]',
    )
  })

  return pickEarliestInstanceByStartDate(matching)?.instance_id || null
}

/** Instance window for a calendar day (includes completed instances). */
export const isInstrumentActiveOnDay = (instances, targetDayMoment) =>
  (instances || []).some((instance) => {
    if (!instance.start_date && !instance.end_date) return false

    if (instance.end_date && !instance.start_date) {
      const endDay = moment(instance.end_date).startOf('day')
      return targetDayMoment.isSameOrBefore(endDay, 'day')
    }

    if (instance.end_date) {
      return targetDayMoment.isBetween(
        instance.start_date,
        instance.end_date,
        'day',
        '[]',
      )
    }

    return targetDayMoment.isSameOrAfter(moment(instance.start_date), 'day')
  })

export const getEffectiveVisibleDays = (instanceGroup, schedule) => {
  const recurringVisibleDays = schedule?.recurring?.visible_days
  if (recurringVisibleDays !== undefined && recurringVisibleDays !== null) {
    return recurringVisibleDays
  }

  return getInstanceVisibleDays(instanceGroup)
}

export const isVisitDeployedOrPersistentSchedule = (schedule) =>
  isPersistentRecurringScheme(schedule?.recurring) ||
  isVisitDeployedInstrument(schedule)

/**
 * Persistent instances (including visit-deployed persistent) may omit
 * start_date after visit confirmation. Treat the calendar window as starting
 * today — never retroactively. Visit-based non-persistent placeholders keep
 * start_date null until confirmation and must stay hidden.
 */
export const getInstanceCalendarStartDay = (
  instanceGroup,
  instance,
  schedule,
  asOfDay = moment(),
) => {
  if (instance?.start_date) {
    return moment(instance.start_date).startOf('day')
  }

  if (!instance?.end_date || !canTreatNullStartDateAsLive(schedule)) {
    return null
  }

  return moment(asOfDay).startOf('day')
}

/** Day-level visibility using visible_days / end_date (matches mobile calendar intent). */
export const isInstanceVisibleOnCalendarDay = (
  instanceGroup,
  instance,
  targetDayMoment,
  schedule,
  asOfDay = moment(),
  firstLogin,
) => {
  if (!instance?.start_date && !instance?.end_date) return false

  if (!instance?.start_date && !canTreatNullStartDateAsLive(schedule)) {
    return false
  }

  const scheduleScopedInstances = getScheduleScopedInstances(
    instanceGroup,
    instance,
  )

  const absoluteVisibility = isAbsoluteDeployScheduleVisibleOnDay(
    schedule,
    targetDayMoment,
    {
      firstLogin,
      areAllInstancesCompleted: Boolean(instance?.completion),
      instances: scheduleScopedInstances,
    },
  )
  if (absoluteVisibility !== null) {
    return absoluteVisibility
  }

  const nowVisibility = isNowDeployScheduleVisibleOnDay(
    schedule,
    targetDayMoment,
    {
      firstLogin,
      areAllInstancesCompleted: Boolean(instance?.completion),
      instances: scheduleScopedInstances,
      asOfDay,
    },
  )
  if (nowVisibility !== null) {
    return nowVisibility
  }

  const startDay = getEffectiveInstanceCalendarStartDay(
    instanceGroup,
    instance,
    schedule,
    firstLogin,
    asOfDay,
  )

  if (!startDay || targetDayMoment.isBefore(startDay, 'day')) return false

  if (instance.end_date) {
    const endDay = moment(instance.end_date).startOf('day')
    if (targetDayMoment.isAfter(endDay, 'day')) return false
  }

  if (!instance.start_date) {
    const visibleDays = getEffectiveVisibleDays(instanceGroup, schedule)
    let lastVisibleDay = startDay.clone().add(visibleDays, 'days')

    if (instance.end_date) {
      const endDay = moment(instance.end_date).startOf('day')
      if (lastVisibleDay.isAfter(endDay)) {
        lastVisibleDay = endDay
      }
    }

    return targetDayMoment.isSameOrBefore(lastVisibleDay, 'day')
  }

  const visibleDays = getEffectiveVisibleDays(instanceGroup, schedule)
  const explicitStartDay = startDay.clone().startOf('day')

  if (instance.end_date && visibleDays === 0) {
    if (isPersistentRecurringScheme(schedule?.recurring)) {
      return true
    }

    return targetDayMoment.isSame(explicitStartDay, 'day')
  }

  const lastVisibleDay = explicitStartDay.clone().add(visibleDays, 'days')
  return targetDayMoment.isSameOrBefore(lastVisibleDay, 'day')
}

export const hasActiveInstanceVisibleOnCalendarDay = (
  instanceGroup,
  instances,
  targetDayMoment,
  schedule,
  firstLogin,
) =>
  (instances || []).some(
    (instance) =>
      !instance.completion &&
      isInstanceVisibleOnCalendarDay(
        instanceGroup,
        instance,
        targetDayMoment,
        schedule,
        moment(),
        firstLogin,
      ),
  )

/** Carousel / today view: instance active only after schedule deploy day (e.g. first_login). */
export const isMultiScheduleInstanceActiveAt = (
  instanceGroup,
  instance,
  schedule,
  firstLogin,
  referenceDate = new Date(),
) => {
  const referenceMoment = moment(referenceDate)
  const deploymentDay = getScheduleDeploymentDay(schedule?.deploy, firstLogin)

  if (deploymentDay && referenceMoment.isBefore(deploymentDay, 'day')) {
    return false
  }

  if (!instance?.start_date) {
    return isInstanceActiveInCurrentWindow(instance, schedule)
  }

  if (!instance?.end_date) return false

  const effectiveStart = deploymentDay
    ? moment.max(moment(instance.start_date).startOf('day'), deploymentDay)
    : moment(instance.start_date).startOf('day')

  if (referenceMoment.isBefore(effectiveStart, 'day')) return false

  return referenceMoment.isSameOrBefore(moment(instance.end_date), 'day')
}

export const hasActiveInstanceOnDay = (instances, targetDayMoment) =>
  (instances || []).some((instance) => {
    if (instance.completion || !instance.start_date) return false

    if (instance.end_date) {
      return targetDayMoment.isBetween(
        instance.start_date,
        instance.end_date,
        'day',
        '[]',
      )
    }

    return targetDayMoment.isSameOrAfter(moment(instance.start_date), 'day')
  })

export const getInstancesCompletedOnDay = (instances, targetDayMoment) =>
  (instances || []).filter(
    (instance) =>
      instance.completion &&
      moment(instance.completion).isSame(targetDayMoment, 'day'),
  )

/**
 * Completions shown on a calendar grid day: match occurrence (start_date) day
 * first, then completion timestamp (legacy / same-day submit).
 */
export const getInstancesCompletedForCalendarDay = (
  instances,
  targetDayMoment,
) => {
  const targetDayKey = getCalendarDayKey(targetDayMoment)

  return (instances || []).filter((instance) => {
    if (!instance?.completion) return false

    const occurrenceKey = getInstanceOccurrenceDayKey(instance.start_date)
    if (occurrenceKey && occurrenceKey === targetDayKey) return true

    return moment(instance.completion).isSame(targetDayMoment, 'day')
  })
}

/** Completed occurrence for this calendar day (by instance start, not completion timestamp). */
export const hasCompletedOccurrenceOnCalendarDay = (
  instances,
  targetDayMoment,
) => {
  const targetDayKey = getCalendarDayKey(targetDayMoment)

  return (instances || []).some(
    (instance) =>
      instance?.completion &&
      getInstanceOccurrenceDayKey(instance.start_date) === targetDayKey,
  )
}

export const getEarliestInstanceStart = (instances, schedule, instanceGroup) => {
  const starts = (instances || [])
    .map((instance) =>
      getInstanceCalendarStartDay(instanceGroup, instance, schedule),
    )
    .filter(Boolean)

  if (!starts.length) return null

  return moment.min(starts)
}

export const getEarliestActiveInstanceStart = (instances) => {
  const activeStarts = (instances || [])
    .filter((instance) => !instance.completion && instance.start_date)
    .map((instance) => moment(instance.start_date))

  if (!activeStarts.length) return null

  return moment.min(activeStarts).startOf('day')
}

const shouldKeepScheduleInstancesPending = (schedule, instances = []) => {
  if (isPersistentRecurringScheme(schedule?.recurring)) return true
  if (!instances.length) return true

  // Never-repeat: one completion means done for this schedule row, even if API
  // instance_id and local review snapshot disagree after submit (GOLD-12341).
  if (!isIntervalRecurringScheme(schedule?.recurring)) {
    if (instances.some((instance) => instance.completion)) return false
  }

  return instances.some((instance) => !instance.completion)
}

const shouldHideScheduleInstancesOnDay = (schedule, instances, targetDayMoment) => {
  if (isPersistentRecurringScheme(schedule?.recurring)) return false
  return getInstancesCompletedForCalendarDay(instances, targetDayMoment).length > 0
}

/**
 * Completed non-persistent instruments belong in the completed calendar section,
 * not pending. Persistent instruments remain pending after a completion so they
 * can be completed again. GOLD-12341: never-repeat instruments must not stay
 * pending when any instance (API or local snapshot) is already completed.
 * GOLD-12455: multi-schedule instruments evaluate pending per schedule instance.
 */
export const shouldKeepInstrumentInCalendarPending = (instrument) => {
  const scheduleMeta = instrument?.metadata?.schedule || instrument?.schedule
  const instances = instrument?.instances || []
  const scheduleId =
    instrument?.schedule_id ||
    instances[0]?.schedule_id ||
    instances[0]?.scheduleId

  if (scheduleId && instances.length <= 1) {
    const schedule = resolveSchedule(scheduleMeta, scheduleId)
    return shouldKeepScheduleInstancesPending(schedule, instances)
  }

  if (isMultiScheduleMetadata(scheduleMeta)) {
    return getScheduleInstanceEntries(instrument).some(
      ({ schedule, instances: entryInstances }) =>
        shouldKeepScheduleInstancesPending(schedule, entryInstances),
    )
  }

  const schedule = resolveSchedule(scheduleMeta, scheduleId)
  return shouldKeepScheduleInstancesPending(schedule, instances)
}

/**
 * GOLD-12341: non-persistent instruments that were completed on this calendar
 * day belong only in the completed section, even when n-days recurrence still
 * matches the day (test HSO / deploy.now).
 * GOLD-12455: only hide the schedule row that was completed, not sibling schedules.
 */
export const shouldHideNonPersistentPendingOnDay = (
  instrument,
  targetDayMoment,
) => {
  const scheduleMeta = instrument?.metadata?.schedule || instrument?.schedule
  const instances = instrument?.instances || []
  const scheduleId =
    instrument?.schedule_id ||
    instances[0]?.schedule_id ||
    instances[0]?.scheduleId

  if (scheduleId && instances.length <= 1) {
    const schedule = resolveSchedule(scheduleMeta, scheduleId)
    return shouldHideScheduleInstancesOnDay(schedule, instances, targetDayMoment)
  }

  if (isMultiScheduleMetadata(scheduleMeta)) {
    return false
  }

  const schedule = resolveSchedule(scheduleMeta, scheduleId)
  return shouldHideScheduleInstancesOnDay(schedule, instances, targetDayMoment)
}

/**
 * Build completed calendar rows for a day.
 * Persistent instruments may have multiple completions on the same day.
 */
export const mapCompletedInstrumentsForDay = (
  completedInstruments,
  targetDayMoment,
) =>
  (completedInstruments || []).flatMap((instrument) => {
    const completionsOnDay = getInstancesCompletedForCalendarDay(
      instrument?.instances,
      targetDayMoment,
    )

    if (!completionsOnDay.length) return []

    const schedule = resolveSchedule(
      instrument?.metadata?.schedule || instrument?.schedule,
      instrument?.schedule_id ||
        instrument?.instances?.[0]?.schedule_id ||
        instrument?.instances?.[0]?.scheduleId,
    )
    if (isPersistentRecurringScheme(schedule?.recurring)) {
      return completionsOnDay.map((instance) => ({
        ...instrument,
        instances: [instance],
      }))
    }

    return [
      {
        ...instrument,
        instances: [completionsOnDay[0]],
      },
    ]
  })

const getEarliestEffectiveInstanceStart = (
  instances,
  schedule,
  instanceGroup,
  firstLogin,
) => {
  const starts = (instances || [])
    .map((instance) =>
      getEffectiveInstanceCalendarStartDay(
        instanceGroup,
        instance,
        schedule,
        firstLogin,
      ),
    )
    .filter(Boolean)

  if (!starts.length) return null

  return moment.min(starts)
}

const isPersistentPendingOnDay = ({
  instrument,
  schedule,
  instances,
  targetDayMoment,
  isToday,
  isTimeOfDayValid,
  firstLogin,
}) => {
  const { deploy } = schedule || {}

  if (hasAbsoluteDeploy(deploy)) {
    return (
      isAbsoluteDeployScheduleVisibleOnDay(schedule, targetDayMoment, {
        isToday,
        isTimeOfDayValid,
        firstLogin,
        areAllInstancesCompleted: false,
        instances,
      }) === true
    )
  }

  if (hasNowDeploy(deploy)) {
    return (
      isNowDeployScheduleVisibleOnDay(schedule, targetDayMoment, {
        isToday,
        isTimeOfDayValid,
        firstLogin,
        areAllInstancesCompleted: false,
        instances,
      }) === true
    )
  }

  const activeOnDay = (instances || []).some((instance) =>
    isInstanceVisibleOnCalendarDay(
      instrument,
      instance,
      targetDayMoment,
      schedule,
      moment(),
      firstLogin,
    ),
  )

  if (activeOnDay) {
    if (isToday && deploy?.time_of_day && !isTimeOfDayValid(deploy.time_of_day)) {
      return false
    }
    return true
  }

  const deploymentDay = getEarliestEffectiveInstanceStart(
    instances,
    schedule,
    instrument,
    firstLogin,
  )
  if (!deploymentDay) return false

  if (targetDayMoment.isBefore(deploymentDay, 'day')) return false

  if (
    isToday &&
    targetDayMoment.isSame(deploymentDay, 'day') &&
    deploy?.time_of_day &&
    !isTimeOfDayValid(deploy.time_of_day)
  ) {
    return false
  }

  const allInstancesHaveEndDate = (instances || []).every(
    (inst) => inst.end_date != null,
  )
  if (allInstancesHaveEndDate) return false

  return true
}

/**
 * Whether a persistent instrument should appear on a calendar day.
 * Visit-deployed non-persistent instruments defer to standard calendar rules.
 * @returns {boolean|null} true/false when handled, null when caller should continue other rules
 */
export const shouldShowPersistentOrVisitInstrumentOnDay = ({
  instrument,
  schedule,
  targetDayMoment,
  isToday,
  isTimeOfDayValid,
  isExpired,
  firstLogin,
}) => {
  const { deploy, expire, recurring } = schedule || {}
  const isPersistent = isPersistentRecurringScheme(recurring)

  if (!isPersistent) return null

  if (isExpired(expire, targetDayMoment, firstLogin)) return false

  const instances = instrument?.instances

  return isPersistentPendingOnDay({
    instrument,
    schedule,
    instances,
    targetDayMoment,
    isToday,
    deploy,
    isTimeOfDayValid,
    firstLogin,
  })
}
